/**
 * The forensic-relevance marker on one note — DECISIONS.md #53 step 2.
 *
 * **The badge is for everybody who reads the note; the button is for a role that may document.** A
 * marker says only "relevant" — no category, no reason — so marking is one press. **Removing one asks
 * first** (review of 2026-10-09): it is a new version, not a deletion, but a mis-tap on somebody
 * else's evidence marker would read afterwards as the marker being taken away, and putting it back is
 * a new marker with a later date. Marking again later is a new marker.
 *
 * **One sentence for every refusal.** The codes are a marker already set by somebody else, a stale
 * version, a note withdrawn meanwhile — all of which the refetch that follows puts right on screen.
 */

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ForensicMarkerRefusedError,
  markNote,
  withdrawMarker,
} from './forensicMarkerApi'
import type { PatientNote } from './patientOverviewApi'

type Phase = 'idle' | 'confirming' | 'working' | 'refused' | 'unavailable'

/** The badge, beside the note's other markers in its header. */
export function ForensicMarkerBadge({ note }: { readonly note: PatientNote }) {
  const { t } = useTranslation(['notes'])

  if (note.forensicMarkerId === null) {
    return null
  }

  return (
    <p className="patient-note__forensic" title={t('notes:forensic.explanation')}>
      {t('notes:forensic.badge')}
    </p>
  )
}

/** The button, among the note's actions. */
export function ForensicMarkerButton({
  note,
  patientId,
  onChanged,
}: {
  readonly note: PatientNote
  readonly patientId: string
  /** Written or refused; the overview refetches either way. */
  readonly onChanged: () => void
}) {
  const { t } = useTranslation(['notes'])
  const [phase, setPhase] = useState<Phase>('idle')

  const live = useRef(true)
  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])

  const marked = note.forensicMarkerId !== null

  // A confirmation for a marker that is no longer there closes itself: somebody else removed it and
  // the overview refreshed. Left open, its button would have nothing to remove (review of 2026-10-12).
  useEffect(() => {
    if (phase === 'confirming' && !marked) {
      setPhase('idle')
    }
  }, [phase, marked])

  const run = (write: Promise<unknown>) => {
    setPhase('working')

    write.then(
      () => {
        if (live.current) {
          setPhase('idle')
          onChanged()
        }
      },
      (cause: unknown) => {
        if (!live.current) {
          return
        }
        setPhase(cause instanceof ForensicMarkerRefusedError ? 'refused' : 'unavailable')
        onChanged()
      },
    )
  }

  /** The main button: marks an unmarked note at once; for a marked one, asks first. */
  const press = () => {
    if (phase === 'working') {
      return
    }

    if (marked) {
      setPhase('confirming')
      return
    }

    run(markNote(patientId, note.noteId, new AbortController().signal))
  }

  /**
   * The confirmation's button: only ever a removal. Deciding from the note again here is what let a
   * stale dialog *mark* a note whose marker somebody else had just removed.
   */
  const confirmWithdraw = () => {
    if (phase !== 'confirming' || note.forensicMarkerId === null || note.forensicMarkerVersionId === null) {
      return
    }

    run(withdrawMarker(patientId, note.forensicMarkerId, note.forensicMarkerVersionId, new AbortController().signal))
  }

  const label =
    phase === 'working'
      ? marked
        ? t('notes:forensic.withdrawing')
        : t('notes:forensic.marking')
      : marked
        ? t('notes:forensic.withdraw')
        : t('notes:forensic.mark')

  if (phase === 'confirming') {
    return (
      <div className="patient-note__forensic-confirm" role="alertdialog" aria-label={t('notes:forensic.confirmQuestion')}>
        <p>{t('notes:forensic.confirmQuestion')}</p>
        <button type="button" onClick={() => setPhase('idle')}>
          {t('notes:forensic.keep')}
        </button>
        <button type="button" onClick={confirmWithdraw}>
          {t('notes:forensic.confirmWithdraw')}
        </button>
      </div>
    )
  }

  return (
    <>
      <button
        type="button"
        disabled={phase === 'working'}
        title={t('notes:forensic.explanation')}
        onClick={press}
      >
        {label}
      </button>

      {(phase === 'refused' || phase === 'unavailable') && (
        <p className="patient-note__forensic-notice" role="alert">
          {phase === 'refused' ? t('notes:forensic.refused') : t('notes:forensic.unavailable')}
        </p>
      )}
    </>
  )
}
